import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { STATUS_LABEL, STATUS_TONE } from '../lib/format';

// ───────────── Toasts ─────────────
type Toast = { id: number; text: string; tone?: 'error' };
const ToastCtx = createContext<(text: string, tone?: 'error') => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone?: 'error') => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, text, tone }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), tone === 'error' ? 7000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`toast ${t.tone ?? ''}`}>{t.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

/** Ejecuta una acción mostrando el error como aviso. */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = useCallback(async <T,>(fn: () => Promise<T>, ok?: string): Promise<T | undefined> => {
    setBusy(true);
    try {
      const r = await fn();
      if (ok) toast(ok);
      return r;
    } catch (e) {
      toast((e as Error).message, 'error');
      return undefined;
    } finally {
      setBusy(false);
    }
  }, [toast]);
  return { busy, run };
}

/** Carga datos con recarga manual (`reload`) y recarga en segundo plano (`refresh`: sin pantalla de carga y sin mostrar errores pasajeros). */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | undefined; error: string | null; loading: boolean; reload: () => void; refresh: () => void } {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const quietRef = useRef(false);
  useEffect(() => {
    let alive = true;
    const quiet = quietRef.current;
    quietRef.current = false;
    if (!quiet) setLoading(true);
    fnRef.current().then((d) => { if (alive) { setData(d); setError(null); } }).catch((e) => { if (alive && !quiet) setError(e.message); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1), refresh: () => { quietRef.current = true; setTick((t) => t + 1); } };
}

// ───────────── Componentes ─────────────
export function StatusBadge({ status, label }: { status: string; label?: string }) {
  return <span className={`badge ${STATUS_TONE[status] ?? ''}`}>{label ?? STATUS_LABEL[status] ?? status}</span>;
}

export function Badge({ tone, children }: { tone?: string; children: ReactNode }) {
  return <span className={`badge ${tone ?? ''}`}>{children}</span>;
}

export function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="row between"><h2>{title}</h2><button className="ghost" onClick={onClose} aria-label="Cerrar">✕</button></div>
        <div className="stack">{children}</div>
        {footer && <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}{hint && <span className="hint">{hint}</span>}</label>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Loading() {
  return <div className="empty">Cargando…</div>;
}

export function ErrorBox({ error }: { error: string | null }) {
  return error ? <div className="alert error">{error}</div> : null;
}

export function PageHead({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div><h1>{title}</h1>{subtitle && <p className="muted">{subtitle}</p>}</div>
      {actions && <div className="row wrap">{actions}</div>}
    </div>
  );
}

export function Kpi({ value, label, hint }: { value: ReactNode; label: string; hint?: string }) {
  return <div className="card kpi" title={hint}><span className="v">{value}</span><span className="l">{label}</span></div>;
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: Array<{ id: T; label: ReactNode }> }) {
  return (
    <div className="tabs" role="tablist">
      {items.map((i) => <button key={i.id} role="tab" aria-selected={value === i.id} className={value === i.id ? 'active' : ''} onClick={() => onChange(i.id)}>{i.label}</button>)}
    </div>
  );
}

export function Severity({ s }: { s: string }) {
  return <Badge tone={s === 'error' ? 'error' : s === 'warning' ? 'warn' : 'info'}>{s === 'error' ? 'Error' : s === 'warning' ? 'Revisar' : 'Info'}</Badge>;
}

export function Confirm({ title, text, onConfirm, onClose, danger, needReason, reasonLabel, reasons }: {
  title: string; text?: ReactNode; onConfirm: (reason: string) => void; onClose: () => void; danger?: boolean; needReason?: boolean; reasonLabel?: string; reasons?: string[];
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal title={title} onClose={onClose} footer={<>
      <button onClick={onClose}>Cancelar</button>
      <button className={danger ? 'danger' : 'primary'} disabled={needReason && reason.trim().length < 3} onClick={() => onConfirm(reason)}>Confirmar</button>
    </>}>
      {text && <p>{text}</p>}
      {needReason && (
        <>
          {reasons && <div className="row wrap">{reasons.map((r) => <button key={r} className="small" onClick={() => setReason(r)}>{r}</button>)}</div>}
          <Field label={reasonLabel ?? 'Motivo'}><textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
        </>
      )}
    </Modal>
  );
}
